import { HoverCard, HoverCardContent, HoverCardTrigger, cn } from "@hulahoop/ui";
import { exceedsAmount } from "@/domain/fiscal/fiscalCalculations";
import type { CreateInvestmentLabels } from "@/domain/types/investmentPiece";
import { QuotaCapacityRow } from "./QuotaCapacityRow";

export interface QuotaCapacity {
  name: string;
  total: number | null;
  completed: number;
  reserved: number;
  available: string | null;
  left: number | null;
}

export function QuotaCapacityMeter({
  quota,
  amount,
  transferredDeduction,
  format,
  labels,
}: {
  quota: QuotaCapacity;
  amount: number;
  transferredDeduction: number;
  format: (value: number) => string;
  labels: CreateInvestmentLabels["meter"];
}) {
  const share = (value: number) =>
    Math.max(0, Math.min(100, quota.total ? (value / quota.total) * 100 : 0));
  const pct = (value: number) => `${share(value)}%`;
  const hasAmount = amount > 0;
  const exceeds = exceedsAmount(String(amount), quota.available);

  return (
    <HoverCard>
      <HoverCardTrigger
        delay={100}
        closeDelay={150}
        render={<div className="cursor-default py-1" aria-label={labels.label} />}
      >
        <div className="relative h-1.5 w-full overflow-hidden rounded-sm bg-muted">
          <div
            className="absolute inset-y-0 left-0 bg-primary/70 transition-all"
            style={{ width: pct(quota.completed) }}
          />
          <div
            className="absolute inset-y-0 bg-warning transition-all"
            style={{ left: pct(quota.completed), width: pct(quota.reserved) }}
          />
          {hasAmount && (
            <div
              className={cn(
                "absolute inset-y-0 animate-pulse transition-all",
                exceeds ? "bg-destructive" : "bg-info",
              )}
              style={{
                left: `${share(quota.completed) + share(quota.reserved)}%`,
                width: pct(exceeds ? Number(quota.available) : amount),
              }}
            />
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-64 space-y-1.5">
        {quota.total !== null && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{labels.total(quota.name)}</span>
            <span className="font-medium text-foreground tabular-nums">{format(quota.total)}</span>
          </div>
        )}
        <QuotaCapacityRow
          color="bg-primary/70"
          label={labels.completed}
          value={format(quota.completed)}
        />
        <QuotaCapacityRow
          color="bg-warning"
          label={labels.reserved}
          value={format(quota.reserved)}
        />
        {hasAmount && (
          <>
            <QuotaCapacityRow
              color={exceeds ? "bg-destructive" : "bg-info"}
              label={labels.investment}
              value={format(amount)}
              alert={exceeds}
            />
            <QuotaCapacityRow
              label={labels.transferredDeduction}
              value={format(transferredDeduction)}
              indented
            />
          </>
        )}
        {quota.left !== null && (
          <QuotaCapacityRow
            color="bg-muted"
            label={labels.available}
            value={format(quota.left)}
            alert={exceeds}
          />
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
